import { difference, isEmpty, repeat } from 'lodash/fp';
import { EditorContent, ImEditorContent, MNElement } from '../../../../common/elements/elementModelTypes';
import {
    getCaption,
    getElementId,
    getElementType,
    getFilename,
    getShowCaption,
    getTextContent,
    getTitle,
} from '../../../../common/elements/utils/elementPropertyUtils';
import { getDomElementId } from '../../../element/utils/elementUtil';
import { asObject } from '../../../../common/utils/immutableHelper';
import { createTaskListClipboardText } from '../../../../common/taskLists/taskListClipboardCopyUtils';
import { isSingleEditorTaskList } from '../../../../common/taskLists/taskListContentVersionUtils';
import { ElementType } from '../../../../common/elements/elementTypes';
import { normaliseClipboardText } from '../../../../common/tiptap/extensions/clipper/ClipboardCopyHandler';
import type { TiptapContent } from '../../../../common/tiptap/tiptapTypes';
import { getEditorJsonContentLines } from '../../../../common/tiptap/utils/jsonContentUtils/getEditorJsonContentLines';

/**
 * Serialises stored editor content while discarding empty trailing blocks.
 */
const getEditorClipboardText = (content: ImEditorContent | EditorContent | string | undefined): string => {
    const lines = getEditorJsonContentLines(content);

    while (lines[lines.length - 1] === '') lines.pop();

    return normaliseClipboardText(lines.join('\n'));
};

const getElementDomNode = (element: MNElement): HTMLElement | null => {
    const elementId = getElementId(element);
    const elementDomId = getDomElementId(elementId);

    return document.getElementById(elementDomId);
};

const getCardClipboardText = (element: MNElement): string => {
    const textContent = getTextContent(element);
    const bodyText = getEditorClipboardText(textContent);
    const captionText = getShowCaption(element) ? getEditorClipboardText(getCaption(element)) : '';
    const contentSections = [bodyText, captionText].filter((text) => text.replace(/\n/g, ''));

    return normaliseClipboardText(contentSections.join('\n'));
};

const getElementInnerText = (element: MNElement): string => {
    const domNode = getElementDomNode(element);
    if (!domNode) return '';

    return domNode.textContent ?? '';
};

const getBoardClipboardText = (element: MNElement): string => {
    return getTitle(element);
};

const getColumnClipboardText = (element: MNElement): string => {
    const domNode = getElementDomNode(element);
    if (!domNode) return '';

    const title = getTitle(element);

    const listDomNode = domNode.querySelector('.List');
    if (!listDomNode) return title;

    const subElements = Array.from(listDomNode.querySelectorAll(':scope > .ListElement .Element'));
    const textContent = subElements
        .map((node) => {
            const isTaskList = node.querySelector('.Element .TaskList') !== null;
            if (isTaskList) return null;

            return node.textContent;
        })
        .filter((str): str is string => str !== null)
        .join('\n');

    return `${title}\n${textContent}`;
};

const getImmediateTasks = (domNode: HTMLElement, depth: number): Element[] => {
    const allTasks = domNode.querySelectorAll('.ListContainer .Task');
    if (isEmpty(allTasks)) return [];

    const allSubTasks = domNode.querySelectorAll(`${repeat(depth, '.ListContainer')} .ListContainer .Task`);

    return difference(allTasks, allSubTasks);
};

const getTaskListsTextContent = (domNode: HTMLElement, depth: number = 1) => {
    const immediateTasks = getImmediateTasks(domNode, depth);
    if (!immediateTasks) return;

    return immediateTasks
        .flatMap((taskNode) => {
            const textNode = taskNode.querySelector('.tiptap');
            if (!textNode) return;

            return `${textNode.textContent}\n`;
        })
        .join('');
};

const getLegacyTaskListClipboardTextContent = (element: MNElement): string => {
    const domNode = getElementDomNode(element);
    if (!domNode) return '';

    const title = getTitle(element);

    return `${title ? title : ''}${getTaskListsTextContent(domNode)}`;
};

// As per `getElementClipboardHtmlString`: a single-editor list has no task DOM nodes to scrape,
// so its text comes from its document - which is also where its title lives.
const getTaskListClipboardTextContent = (element: MNElement): string => {
    if (!isSingleEditorTaskList(element)) return getLegacyTaskListClipboardTextContent(element);

    const textContent = asObject<TiptapContent | undefined>(getTextContent(element));

    return textContent ? createTaskListClipboardText(textContent) : '';
};

const getFileClipboardText = (element: MNElement) => getFilename(element) ?? getTitle(element);

const getCommentThreadClipboardText = (element: MNElement): string => {
    const domNode = getElementDomNode(element);
    if (!domNode) return '';

    // Get all comment text from the comment thread
    const commentNodes = domNode.querySelectorAll('.Comment .tiptap');
    if (!commentNodes || commentNodes.length === 0) return '';

    return Array.from(commentNodes)
        .map((node) => node.textContent || '')
        .filter(Boolean)
        .join('\n');
};

type ClipboardTextReader = (element: MNElement) => string;
const clipboardTextReaders: Partial<Record<ElementType, ClipboardTextReader>> = {
    [ElementType.CARD_TYPE]: getCardClipboardText,
    [ElementType.BOARD_TYPE]: getBoardClipboardText,
    [ElementType.COLUMN_TYPE]: getColumnClipboardText,
    [ElementType.IMAGE_TYPE]: getElementInnerText,
    [ElementType.LINE_TYPE]: getElementInnerText,
    [ElementType.TASK_LIST_TYPE]: getTaskListClipboardTextContent,
    [ElementType.FILE_TYPE]: getFileClipboardText,
    [ElementType.DOCUMENT_TYPE]: getFileClipboardText,
    [ElementType.COMMENT_THREAD_TYPE]: getCommentThreadClipboardText,
};

export default (element: MNElement): string | null => {
    const elementType: ElementType = getElementType(element);
    const clipboardReader = clipboardTextReaders[elementType];
    if (!clipboardReader) return null;

    const text = clipboardReader(element);
    return isEmpty(text) ? null : text;
};
